import type {
  TestimonialAskMoment,
  TestimonialNameMode,
  TestimonialResource,
  TestimonialSentiment,
  TestimonialStateResource,
} from "@/api/generated";

export type { TestimonialAskMoment, TestimonialNameMode, TestimonialSentiment };
export type Testimonial = TestimonialResource;
export type TestimonialStatus = Testimonial["status"];
type TestimonialState = TestimonialStateResource;
export type TestimonialPreview = TestimonialState["preview"];

export type TestimonialDraft = {
  sentiment: TestimonialSentiment | null;
  body: string;
  nameMode: TestimonialNameMode;
  showTreeSize: boolean;
  consent: boolean;
};

export type TestimonialPayload = {
  sentiment: TestimonialSentiment;
  body: string;
  consent: boolean;
  name_mode: TestimonialNameMode;
  show_tree_size: boolean;
  moment: TestimonialAskMoment | null;
};

export type PublicTestimonial = {
  body: string;
  author: string;
  tree_size: number | null;
  source: "app" | "web";
};

const TESTIMONIAL_PUBLIC_MIN_LENGTH = 20;
const TESTIMONIAL_PUBLIC_MAX_LENGTH = 300;
const TESTIMONIAL_PRIVATE_MAX_LENGTH = 1000;

const treeSizeFormatter = new Intl.NumberFormat("en-US");

export const testimonialSentiments: { value: TestimonialSentiment; label: string }[] = [
  { value: "great", label: "ممتازة" },
  { value: "good", label: "جيدة" },
  { value: "improve", label: "تحتاج إلى تحسين" },
];

export const emptyTestimonialDraft = (): TestimonialDraft => ({
  sentiment: null,
  body: "",
  nameMode: "short",
  showTreeSize: true,
  consent: false,
});

/** Improvement notes always stay with the team, so only positive answers can become public words. */
export const isPositiveSentiment = (sentiment: TestimonialSentiment | null) =>
  sentiment === "great" || sentiment === "good";

export const testimonialMaxLength = (draft: TestimonialDraft) =>
  isPositiveSentiment(draft.sentiment) ? TESTIMONIAL_PUBLIC_MAX_LENGTH : TESTIMONIAL_PRIVATE_MAX_LENGTH;

/** Arabic count wording for a tree size, for example "شجرة من 14 فردًا" or "شجرة من 210 أفراد". */
export const formatTreeSize = (count: number) => {
  const number = treeSizeFormatter.format(count);
  const lastTwo = count % 100;

  if (count === 1) {
    return "شجرة من فرد واحد";
  }

  if (count === 2) {
    return "شجرة من فردين";
  }

  if (lastTwo >= 3 && lastTwo <= 10) {
    return `شجرة من ${number} أفراد`;
  }

  return lastTwo === 0 || lastTwo <= 2 ? `شجرة من ${number} فرد` : `شجرة من ${number} فردًا`;
};

/** Byline choices; the full name is offered only when it adds something beyond the first and family name. */
export const testimonialNameOptions = (preview: TestimonialPreview) =>
  [
    { value: "short" as const, name: preview.short_name, hint: "الاسم الأول واسم العائلة" },
    { value: "full" as const, name: preview.full_name, hint: "الاسم الكامل" },
    { value: "anonymous" as const, name: preview.anonymous_name, hint: "بدون اسم" },
  ].filter((option) => option.value !== "full" || preview.full_name !== preview.short_name);

export const testimonialByline = (preview: TestimonialPreview, mode: TestimonialNameMode) =>
  testimonialNameOptions(preview).find((option) => option.value === mode)?.name ?? preview.short_name;

/**
 * Checks a draft before sending. A public word needs consent and 20 characters; a team-only note needs text only.
 * Returns null when the draft can be sent.
 */
export const testimonialDraftError = (draft: TestimonialDraft, teamOnly: boolean): string | null => {
  const body = draft.body.trim();

  if (!draft.sentiment) {
    return "اختر كيف كانت تجربتك.";
  }

  if (body === "") {
    return isPositiveSentiment(draft.sentiment) ? "اكتب كلمتك أولًا." : "اكتب ملاحظتك أولًا.";
  }

  if (body.length > testimonialMaxLength(draft)) {
    return `اكتب ${testimonialMaxLength(draft)} حرف أو أقل.`;
  }

  if (!isPositiveSentiment(draft.sentiment) || teamOnly) {
    return null;
  }

  if (body.length < TESTIMONIAL_PUBLIC_MIN_LENGTH) {
    return `اكتب ${TESTIMONIAL_PUBLIC_MIN_LENGTH} حرفًا على الأقل.`;
  }

  return draft.consent ? null : "وافق على النشر، أو أرسلها للفريق فقط.";
};

export const testimonialPayload = (
  draft: TestimonialDraft,
  teamOnly: boolean,
  moment: TestimonialAskMoment | null,
): TestimonialPayload => ({
  sentiment: draft.sentiment ?? "improve",
  body: draft.body.trim(),
  consent: isPositiveSentiment(draft.sentiment) && !teamOnly && draft.consent,
  name_mode: draft.nameMode,
  show_tree_size: draft.showTreeSize,
  moment,
});

/** A word the author can still pull from the site. */
export const canWithdrawTestimonial = (testimonial: Testimonial | null) =>
  testimonial?.status === "pending" || testimonial?.status === "approved";

export const canRepublishTestimonial = (testimonial: Testimonial | null) => testimonial?.status === "withdrawn";

/** Rejected words leave the author free to write a fresh one. */
export const canWriteTestimonial = (testimonial: Testimonial | null) =>
  testimonial === null || testimonial.status === "rejected";

export const testimonialStatusNote = (testimonial: Testimonial) => {
  switch (testimonial.status) {
    case "pending":
      return "نراجعها قبل النشر في موقع أوراق.";
    case "approved":
      return "منشورة في موقع أوراق.";
    case "withdrawn":
      return "أخفيت كلمتك من موقع أوراق.";
    case "rejected":
      return "لم نتمكن من نشر هذه الكلمة. يمكنك كتابة كلمة جديدة.";
    default:
      return "وصلت لفريق أوراق فقط، ولن تُنشر.";
  }
};

export const testimonialAskHeadline = (moment: TestimonialAskMoment) =>
  moment === "paper_tree_delivered" ? "وصلتك الشجرة الورقية. ما رأيك في أوراق؟" : "ما رأيك في أوراق إلى الآن؟";

export const testimonialSentMessage = (testimonial: Testimonial) =>
  testimonial.status === "private"
    ? { title: "وصلت كلمتك لفريق أوراق", body: "وصلت لفريق أوراق فقط، ولن تُنشر." }
    : {
        title: "وصلتنا كلمتك",
        body: "نراجعها قبل النشر في موقع أوراق. يمكنك إخفاؤها متى شئت من «حسابي».",
      };

/** The first validation message from a Laravel JSON error, falling back to its message or a generic line. */
export const testimonialErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { message?: unknown; errors?: Record<string, unknown> } } })?.response
    ?.data;
  const firstError = Object.values(data?.errors ?? {})
    .flat()
    .find((message): message is string => typeof message === "string" && message !== "");

  return firstError ?? (typeof data?.message === "string" && data.message !== "" ? data.message : fallback);
};
